// src/components/dashboard/CardItem.tsx
import React, { memo } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { CardData } from "../../types/card.types";

interface CardItemProps {
  card: CardData;
  index: number;
  onClick: (cardId: string) => void;
}

const STATUS_COLORS = {
  draft: "text-gray-400",
  paid: "text-blue-400",
  sent: "text-yellow-400",
  opened: "text-green-400",
  received: "text-purple-400",
} as const;

export const CardItem = memo(({ card, index, onClick }: CardItemProps) => {
  const rotationAngle = index % 2 === 0 ? 2 : -2;
  const statusColor = STATUS_COLORS[card.status] || "text-gray-400";

  return (
    <motion.div
      layout
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1, rotateZ: rotationAngle }}
      whileHover={{ y: -15, rotateZ: 0, zIndex: 50, scale: 1.02 }}
      transition={{ duration: 0.3 }}
      className="relative aspect-[3/4] w-full group cursor-pointer"
      onClick={() => onClick(card.id)}
    >
      <div className="w-full h-full rounded-[32px] overflow-hidden relative shadow-2xl border border-white/10 bg-[#0f0f11] transition-shadow duration-500 group-hover:shadow-yellow-400/10">
        {card.recipientImage ? (
          <img
            src={card.recipientImage}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 transition-all duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-black" />
        )}

        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/90 z-10" />

        <div className="absolute inset-0 p-6 flex flex-col justify-between z-20">
          <div className="flex justify-between items-start">
            <div className="bg-black/60 backdrop-blur-md px-2 py-1 rounded-lg flex items-center gap-1.5 border border-white/10">
              <Star size={10} fill="#fbbf24" className="text-yellow-400" />
              <span className="text-[10px] font-black italic">4.7</span>
            </div>
            <div className="max-w-[70%] text-right">
              <h4 className="text-lg font-black italic uppercase leading-none truncate">
                {card.recipientName}
              </h4>
              <div className="h-1 w-6 bg-yellow-400 mt-2 ml-auto rounded-full" />
            </div>
          </div>

          <div className="flex justify-between items-end">
            <div className="w-9 h-9 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center group-hover:border-yellow-400 transition-colors">
              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
            </div>
            <div className="text-right">
              <p className="text-[7px] font-black uppercase opacity-40 tracking-widest">
                Status
              </p>
              <p className={`text-[10px] font-black uppercase ${statusColor}`}>
                {card.status}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

CardItem.displayName = "CardItem";
